import type { Computer } from '@state/assignmentApi';
import classNames from 'classnames';
import type { FC } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './LauncherUpdateNotice.scss';

/** Simulators whose running launcher is older than the latest release (backend-computed). */
export const getOutdatedComputers = (computers: Computer[] | undefined): Computer[] =>
    (computers ?? []).filter((computer) => computer.launcher_update_required === true);

/**
 * Stable key for the current set of outdated simulators: a dismissed notice comes back
 * when another simulator falls behind or a newer launcher is released.
 */
export const getOutdatedKey = (outdated: Computer[]): string =>
    outdated
        .map((computer) => `${computer.computer_id}:${computer.launcher_version ?? ''}`)
        .sort()
        .join(',') +
    '>' +
    (outdated[0]?.latest_launcher_version ?? '');

interface LauncherUpdateNoticeProps {
    computers: Computer[] | undefined;
    /** When set, the notice shows a close button. */
    onClose?: () => void;
    className?: string;
}

export const LauncherUpdateNotice: FC<LauncherUpdateNoticeProps> = ({ computers, onClose, className }) => {
    const { t } = useTranslation();
    const outdated = getOutdatedComputers(computers);

    if (outdated.length === 0) {
        return null;
    }

    return (
        <Alert
            variant="warning"
            dismissible={Boolean(onClose)}
            onClose={onClose}
            className={classNames('launcher-update-notice', className)}
            data-testid="launcher-update-notice"
        >
            <strong>{t('launcherUpdate.title')}</strong>{' '}
            {t('launcherUpdate.message', {
                computers: outdated.map((computer) => computer.computer_name).join(', '),
                version: outdated[0].latest_launcher_version ?? '',
            })}
        </Alert>
    );
};
